import type { FC, ComponentType } from "react";

import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Routes, useLocation, BrowserRouter } from "react-router-dom";

import ScrollToTop from "./components/ScrollToTop";
import SplashScreen from "./components/SplashScreen";
import { AuthGuard } from "./contexts/auth/auth-guard";
import { GuestGuard } from "./contexts/auth/guest-guard";
import { AuthProvider } from "./contexts/auth/auth-provider";
import { WorkspaceProvider } from "./contexts/workspace-context";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ClientAccessProvider } from "./contexts/client-access-context";
import { ClientProtectedRoute } from "./components/client/ClientProtectedRoute";

interface LazyComponentProps {
  component: ComponentType<any>;
  [key: string]: any;
}

// Public pages bundle
const Index = lazy(
  () => import(/* webpackChunkName: "public" */ "./pages/Index")
);
const Features = lazy(
  () => import(/* webpackChunkName: "public" */ "./pages/Features")
);
const Pricing = lazy(
  () => import(/* webpackChunkName: "public" */ "./pages/Pricing")
);
const FAQPage = lazy(
  () => import(/* webpackChunkName: "public" */ "./pages/FAQPage")
);
const Contact = lazy(
  () => import(/* webpackChunkName: "public" */ "./pages/Contact")
);
const NotFound = lazy(
  () => import(/* webpackChunkName: "public" */ "./pages/NotFound")
);
const Demo = lazy(
  () => import(/* webpackChunkName: "public" */ "./pages/Demo")
);

// Subscription pages bundle
const SubscriptionSuccess = lazy(
  () =>
    import(/* webpackChunkName: "subscription" */ "./pages/SubscriptionSuccess")
);
const SubscriptionCancel = lazy(
  () =>
    import(/* webpackChunkName: "subscription" */ "./pages/SubscriptionCancel")
);

// Auth pages bundle
const Login = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/Login")
);
const Signup = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/Signup")
);
const ForgotPassword = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/ForgotPassword")
);
const ResetPassword = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/ResetPassword")
);
const VerifyEmail = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/VerifyEmail")
);
const RegistrationConfirmation = lazy(
  () =>
    import(
      /* webpackChunkName: "auth" */ "./components/RegistrationConfirmation"
    )
);

// Legal pages bundle
const TermsOfService = lazy(
  () => import(/* webpackChunkName: "legal" */ "./pages/TermsOfService")
);
const PrivacyPolicy = lazy(
  () => import(/* webpackChunkName: "legal" */ "./pages/PrivacyPolicy")
);

// Dashboard pages bundle
const Dashboard = lazy(
  () => import(/* webpackChunkName: "dashboard" */ "./pages/Dashboard")
);
const Projects = lazy(
  () => import(/* webpackChunkName: "dashboard" */ "./pages/Projects")
);
const Notifications = lazy(
  () => import(/* webpackChunkName: "dashboard" */ "./pages/Notifications")
);
const Clients = lazy(
  () => import(/* webpackChunkName: "dashboard" */ "./pages/Clients")
);
const ClientDetail = lazy(
  () => import(/* webpackChunkName: "dashboard" */ "./pages/ClientDetail")
);
const ProjectWorkspace = lazy(
  () => import(/* webpackChunkName: "project" */ "./pages/ProjectWorkspace")
);
const ProjectActivity = lazy(
  () => import(/* webpackChunkName: "project" */ "./pages/ProjectActivity")
);
const ChangeRequestCenter = lazy(
  () =>
    import(/* webpackChunkName: "dashboard" */ "./pages/ChangeRequestCenter")
);
// const MeetingTranscripts = lazy(
//   () => import(/* webpackChunkName: "dashboard" */ "./pages/MeetingTranscripts")
// );
// const TranscriptDetail = lazy(
//   () => import(/* webpackChunkName: "dashboard" */ "./pages/TranscriptDetail")
// );
const Settings = lazy(
  () => import(/* webpackChunkName: "dashboard" */ "./pages/Settings")
);

// Client pages bundle
const ClientAccessGate = lazy(
  () => import(/* webpackChunkName: "client" */ "./pages/ClientAccessGate")
);
const ClientProjectView = lazy(
  () => import(/* webpackChunkName: "client" */ "./pages/ClientProjectView")
);
const ClientPortalDemo = lazy(
  () => import(/* webpackChunkName: "client" */ "./pages/ClientPortalDemo")
);

// Billing pages bundle
const BillingSuccess = lazy(
  () => import(/* webpackChunkName: "billing" */ "./pages/BillingSuccess")
);
const BillingError = lazy(
  () => import(/* webpackChunkName: "billing" */ "./pages/BillingError")
);

// Feedback bundle
const Feedback = lazy(
  () => import(/* webpackChunkName: "feedback" */ "./pages/Feedback")
);

// Preload important parts of the application
const dashboardBundle = {
  preload: () => {
    import("./pages/Dashboard");
    import("./pages/Projects");
    import("./pages/ProjectWorkspace");
  },
};

const PreloadHandler: FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Preload dashboard components when user logs in
    if (location.pathname === "/login" || location.pathname === "/signup") {
      const preloadTimeout = setTimeout(() => {
        dashboardBundle.preload();
      }, 2000);

      return () => clearTimeout(preloadTimeout);
    }
  }, [location]);

  return null;
};

const LazyComponent: FC<LazyComponentProps> = ({
  component: Component,
  ...props
}) => (
  <Suspense fallback={<SplashScreen />}>
    <Component {...props} />
  </Suspense>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <VercelAnalytics />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <WorkspaceProvider>
            <ClientAccessProvider>
              <NotificationProvider>
                <ScrollToTop />
                <PreloadHandler />
                <Routes>
                  {/* Public routes */}
                  <Route
                    path="/"
                    element={<LazyComponent component={Index} />}
                  />
                  <Route
                    path="/features"
                    element={<LazyComponent component={Features} />}
                  />
                  <Route
                    path="/pricing"
                    element={<LazyComponent component={Pricing} />}
                  />
                  <Route
                    path="/faq"
                    element={<LazyComponent component={FAQPage} />}
                  />
                  <Route
                    path="/contact"
                    element={<LazyComponent component={Contact} />}
                  />
                  <Route
                    path="/demo"
                    element={<LazyComponent component={Demo} />}
                  />
                  <Route
                    path="/terms-of-service"
                    element={<LazyComponent component={TermsOfService} />}
                  />
                  <Route
                    path="/privacy-policy"
                    element={<LazyComponent component={PrivacyPolicy} />}
                  />
                  <Route
                    path="/feedback"
                    element={<LazyComponent component={Feedback} />}
                  />

                  {/* Auth routes */}
                  <Route
                    path="/login"
                    element={
                      <GuestGuard>
                        <LazyComponent component={Login} />
                      </GuestGuard>
                    }
                  />
                  <Route
                    path="/signup"
                    element={
                      <GuestGuard>
                        <LazyComponent component={Signup} />
                      </GuestGuard>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <GuestGuard>
                        <LazyComponent component={ForgotPassword} />
                      </GuestGuard>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <GuestGuard>
                        <LazyComponent component={ResetPassword} />
                      </GuestGuard>
                    }
                  />
                  <Route
                    path="/verify-email"
                    element={
                      <GuestGuard>
                        <LazyComponent component={VerifyEmail} />
                      </GuestGuard>
                    }
                  />
                  <Route
                    path="/auth/confirm-registration"
                    element={
                      <AuthGuard>
                        <LazyComponent component={RegistrationConfirmation} />
                      </AuthGuard>
                    }
                  />

                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <AuthGuard>
                        <LazyComponent component={Dashboard} />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <AuthGuard>
                        <LazyComponent component={Notifications} />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <AuthGuard>
                        <LazyComponent component={Projects} />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/projects/:projectId/:clientId"
                    element={
                      <AuthGuard>
                        <LazyComponent component={ProjectWorkspace} />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/projects/:projectId/activity"
                    element={
                      <AuthGuard>
                        <LazyComponent component={ProjectActivity} />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/change-requests"
                    element={
                      <AuthGuard>
                        <LazyComponent component={ChangeRequestCenter} />
                      </AuthGuard>
                    }
                  />
                  {/* <Route
                    path="/transcripts"
                    element={
                      <AuthGuard>
                        <LazyComponent component={MeetingTranscripts} />
                      </AuthGuard>
                    }
                  /> */}
                  {/* <Route
                    path="/transcripts/:id"
                    element={
                      <AuthGuard>
                        <LazyComponent component={TranscriptDetail} />
                      </AuthGuard>
                    }
                  /> */}
                  <Route
                    path="/clients"
                    element={
                      <AuthGuard>
                        <LazyComponent component={Clients} />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/clients/:id"
                    element={
                      <AuthGuard>
                        <LazyComponent component={ClientDetail} />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <AuthGuard>
                        <LazyComponent component={Settings} />
                      </AuthGuard>
                    }
                  />

                  {/* Client access routes */}
                  <Route
                    path="/client-access"
                    element={<LazyComponent component={ClientAccessGate} />}
                  />
                  <Route
                    path="/client/:clientId/project/:projectId"
                    element={
                      <ClientProtectedRoute>
                        <LazyComponent component={ClientProjectView} />
                      </ClientProtectedRoute>
                    }
                  />
                  <Route
                    path="/client-portal-demo"
                    element={<LazyComponent component={ClientPortalDemo} />}
                  />

                  {/* Billing routes */}
                  <Route
                    path="/billing-success"
                    element={<LazyComponent component={BillingSuccess} />}
                  />
                  <Route
                    path="/billing-error"
                    element={<LazyComponent component={BillingError} />}
                  />

                  {/* Subscription routes */}
                  <Route
                    path="/subscription/success"
                    element={<LazyComponent component={SubscriptionSuccess} />}
                  />
                  <Route
                    path="/subscription/cancel"
                    element={<LazyComponent component={SubscriptionCancel} />}
                  />

                  {/* Catch-all route */}
                  <Route
                    path="*"
                    element={<LazyComponent component={NotFound} />}
                  />
                </Routes>
              </NotificationProvider>
            </ClientAccessProvider>
          </WorkspaceProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
